/**
 * assets/header.js — header runtime. 424 lines.
 * Expects window.Clyro from layout/theme.liquid.
 *
 * ZONE MAP (grep "ZONE:" to jump):
 *   ZONE: initHeader   ~line 6   — main init, event delegation
 *   ZONE: menu         ~line 13  — mobile menu open/close/overlay
 *   ZONE: search       ~line 107 — search dialog open/close/predictive
 *   ZONE: makeResult   ~line 199 — builds one predictive search result DOM node
 *   ZONE: fetch        ~line 280 — debounced Shopify predictive search fetch
 *   ZONE: keyboard     ~line 310 — ↑/↓ arrow nav + Enter/Escape
 *   ZONE: cart-count   ~line 380 — badge update on cart change event
 *   ZONE: boot         ~line 415 — section init + editor reload guard
 *
 * DATA HOOKS (read from DOM — never rename without updating header.liquid):
 *   [data-clyro-mobile-menu]    — mobile menu root
 *   [data-clyro-menu-open]      — hamburger trigger
 *   [data-clyro-menu-close]     — close button(s)
 *   [data-clyro-menu-overlay]   — backdrop
 *   [data-clyro-search-open]    — search open trigger
 *   [data-clyro-search-form]    — search <form>
 *   [data-clyro-search-input]   — search <input>
 *   [data-clyro-search-results] — results container
 *   [data-clyro-search-list]    — list of result items
 *   [data-clyro-search-item]    — one result (keyboard nav target)
 *   [data-clyro-cart-open]      — cart trigger
 *   [data-clyro-cart-count]     — badge text node
 *
 * SETTINGS read from dialog data-* attributes:
 *   data-predictive-enabled     — settings.predictive_search_enabled
 *   data-show-vendor            — settings.predictive_search_show_vendor
 *   data-show-price             — settings.predictive_search_show_price
 */
(function () {
  // ZONE: initHeader
  function initHeader(root) {
    if (!root || root.dataset.clyroBound === '1') return;
    root.dataset.clyroBound = '1';

    var menuRoot = root.querySelector('[data-clyro-mobile-menu]');
    var searchDialog = root.querySelector('.clyro-header__search-dialog');

    function setMenuOpen(open) {
      if (!menuRoot) return;
      menuRoot.classList.toggle('is-open', open);
      menuRoot.setAttribute('aria-hidden', open ? 'false' : 'true');
      var panel = menuRoot.querySelector('.clyro-mobile-menu__panel');
      var overlay = menuRoot.querySelector('.clyro-mobile-menu__overlay');
      if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (overlay) overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        if (window.Clyro && Clyro.scrollLock) Clyro.scrollLock.lock('menu');
      } else if (window.Clyro && Clyro.scrollLock) {
        Clyro.scrollLock.unlock('menu');
      }
    }

    function closeMenu() {
      setMenuOpen(false);
    }

    function openMenu() {
      setMenuOpen(true);
    }

    root.addEventListener('click', function (e) {
      var cartTrigger = e.target.closest('[data-clyro-cart-open]');
      if (cartTrigger) {
        e.preventDefault();
        closeMenu();
        if (window.Clyro && Clyro.cart) Clyro.cart.open();
        return;
      }

      var searchOpen = e.target.closest('[data-clyro-search-open]');
      var menuClose = e.target.closest('[data-clyro-menu-close]');

      if (menuClose) {
        e.preventDefault();
        closeMenu();
      }
      if (searchOpen) {
        e.preventDefault();
        setTimeout(function () {
          openSearchDialog();
          var input = searchDialog && searchDialog.querySelector('[data-clyro-search-input]');
          if (input && input.value.trim().length >= 2) {
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }, menuClose ? 200 : 0);
        return;
      }
      if (menuClose) return;

      var menuOpen = e.target.closest('[data-clyro-menu-open]');
      if (menuOpen) {
        e.preventDefault();
        openMenu();
        return;
      }

      var searchClose = e.target.closest('[data-clyro-search-close]');
      if (searchClose) {
        e.preventDefault();
        closeSearchDialog();
      }
    });

    if (menuRoot) {
      menuRoot.addEventListener('click', function (e) {
        if (e.target.closest('[data-clyro-menu-overlay]')) closeMenu();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!menuRoot || !menuRoot.classList.contains('is-open')) return;
      e.preventDefault();
      closeMenu();
    });

    function openSearchDialog() {
      if (!searchDialog || !searchDialog.showModal) return;
      try { searchDialog.showModal(); } catch (_) {}
      if (window.Clyro && Clyro.scrollLock) Clyro.scrollLock.lock('search');
      var input = searchDialog.querySelector('[data-clyro-search-input]');
      if (input) {
        try { input.focus({ preventScroll: true }); } catch (_) { try { input.focus(); } catch (__) {} }
        if (input.value) input.select();
      }
    }

    function closeSearchDialog() {
      if (searchDialog && searchDialog.close) searchDialog.close();
    }

    if (searchDialog) {
      searchDialog.addEventListener('click', function (e) {
        if (e.target === searchDialog) closeSearchDialog();
      });

      searchDialog.addEventListener('close', function () {
        if (window.Clyro && Clyro.scrollLock) Clyro.scrollLock.unlock('search');
        resetSearchUI();
      });

      var searchForm = searchDialog.querySelector('[data-clyro-search-form]');
      var searchInput = searchDialog.querySelector('[data-clyro-search-input]');
      var searchClear = searchDialog.querySelector('[data-clyro-search-clear]');
      var searchIdle = searchDialog.querySelector('[data-clyro-search-idle]');
      var searchLoading = searchDialog.querySelector('[data-clyro-search-loading]');
      var searchResults = searchDialog.querySelector('[data-clyro-search-results]');
      var searchList = searchDialog.querySelector('[data-clyro-search-list]');
      var searchEmpty = searchDialog.querySelector('[data-clyro-search-empty]');
      var searchFooter = searchDialog.querySelector('[data-clyro-search-footer]');
      var searchViewAll = searchDialog.querySelector('[data-clyro-search-view-all]');
      var timer = null;
      var activeIndex = -1;
      var lastProducts = [];
      var showVendor = searchDialog.getAttribute('data-show-vendor') === 'true';
      var showPrice = searchDialog.getAttribute('data-show-price') !== 'false';

      function show(el) { if (el) el.classList.remove('clyro-hidden'); }
      function hide(el) { if (el) el.classList.add('clyro-hidden'); }

      function clearSearchResults() {
        if (searchClear) hide(searchClear);
        show(searchIdle);
        hide(searchLoading);
        hide(searchResults);
        hide(searchEmpty);
        hide(searchFooter);
        if (searchList) searchList.replaceChildren();
        activeIndex = -1;
        lastProducts = [];
      }

      function resetSearchUI() {
        if (searchInput) searchInput.value = '';
        clearSearchResults();
        updateClearButton();
      }

      function setPanel(mode) {
        if (mode === 'idle') {
          show(searchIdle);
          hide(searchLoading);
          hide(searchResults);
          return;
        }
        if (mode === 'loading') {
          hide(searchIdle);
          show(searchLoading);
          hide(searchResults);
          return;
        }
        hide(searchIdle);
        hide(searchLoading);
        show(searchResults);
      }

      function updateClearButton() {
        if (!searchClear || !searchInput) return;
        if (searchInput.value.trim()) show(searchClear);
        else hide(searchClear);
      }

      function updateViewAllLink(q) {
        if (!searchViewAll || !searchForm) return;
        var action = searchForm.getAttribute('action') || '/search';
        var url = new URL(action, window.location.origin);
        if (q) url.searchParams.set('q', q);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        searchViewAll.href = url.pathname + url.search;
      }

      function productImageUrl(p) {
        if (!p) return '';
        if (p.featured_image && p.featured_image.url) return p.featured_image.url;
        if (typeof p.featured_image === 'string') return p.featured_image;
        if (p.image && p.image.url) return p.image.url;
        if (typeof p.image === 'string') return p.image;
        if (p.images && p.images[0]) {
          var first = p.images[0];
          return typeof first === 'string' ? first : (first.url || '');
        }
        return '';
      }

      function makeResultItem(p, index) {
        var a = document.createElement('a');
        a.href = p.url || '#';
        a.setAttribute('role', 'option');
        a.setAttribute('data-clyro-search-item', '');
        a.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
        a.className = 'flex items-center gap-3 px-3 py-2.5 rounded-lg clyro-motion';
        a.style.cssText = 'color: inherit; text-decoration: none; border-radius: calc(var(--input-radius) * 0.75);';

        var imgWrap = document.createElement('div');
        imgWrap.className = 'shrink-0 overflow-hidden';
        imgWrap.style.cssText = 'width: 52px; height: 52px; border-radius: calc(var(--card-radius) * 0.5); background: color-mix(in srgb, currentColor 6%, transparent);';

        var imgUrl = productImageUrl(p);
        if (imgUrl) {
          var img = document.createElement('img');
          img.src = imgUrl;
          img.alt = p.title || '';
          img.loading = 'lazy';
          img.width = 52;
          img.height = 52;
          img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
          imgWrap.appendChild(img);
        } else {
          imgWrap.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;opacity:0.35;"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>';
        }

        var meta = document.createElement('div');
        meta.className = 'flex-1 min-w-0';

        var titleRow = document.createElement('div');
        titleRow.className = 'flex items-start gap-2';

        var title = document.createElement('span');
        title.textContent = p.title || '';
        title.className = 'flex-1 text-[14px] font-medium leading-snug line-clamp-2';

        titleRow.appendChild(title);

        if (p.available === false) {
          var badge = document.createElement('span');
          badge.textContent = 'Sold out';
          badge.className = 'shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full';
          badge.style.cssText = 'background: color-mix(in srgb, currentColor 12%, transparent); opacity: 0.75;';
          titleRow.appendChild(badge);
        }

        meta.appendChild(titleRow);

        if (showVendor && p.vendor) {
          var vendorEl = document.createElement('p');
          vendorEl.textContent = p.vendor;
          vendorEl.className = 'text-[11px] uppercase tracking-wide opacity-45 mt-0.5';
          meta.appendChild(vendorEl);
        }

        if (showPrice) {
          var priceRow = document.createElement('div');
          priceRow.className = 'mt-1 flex items-baseline gap-2 flex-wrap';

          var price = document.createElement('span');
          price.textContent = p.price;
          price.className = 'text-sm font-semibold tabular-nums';

          priceRow.appendChild(price);

          if (p.compare_at_price && Number(p.compare_at_price) > Number(p.price)) {
            var compare = document.createElement('span');
            compare.textContent = p.compare_at_price;
            compare.className = 'text-xs opacity-45 line-through tabular-nums';
            priceRow.appendChild(compare);
          }

          meta.appendChild(priceRow);
        }
        a.appendChild(imgWrap);
        a.appendChild(meta);

        a.addEventListener('mouseenter', function () {
          setActiveIndex(index);
        });

        return a;
      }

      function setActiveIndex(index) {
        activeIndex = index;
        if (!searchList) return;
        Array.prototype.forEach.call(searchList.querySelectorAll('[data-clyro-search-item]'), function (el, i) {
          el.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
        });
      }

      function renderProducts(products, q) {
        lastProducts = products || [];
        if (!searchList) return;
        searchList.replaceChildren();
        updateViewAllLink(q);

        if (!lastProducts.length) {
          hide(searchFooter);
          show(searchEmpty);
          setPanel('results');
          return;
        }

        hide(searchEmpty);
        show(searchFooter);
        lastProducts.forEach(function (p, i) {
          searchList.appendChild(makeResultItem(p, i));
        });
        setActiveIndex(0);
        setPanel('results');
      }

      function runSearch(q) {
        updateViewAllLink(q);
        if (!q || q.length < 2) {
          clearSearchResults();
          updateClearButton();
          if (searchInput && searchInput.value.trim()) {
            hide(searchIdle);
          }
          return;
        }
        setPanel('loading');
        Clyro.searchProducts(q).then(function (products) {
          if (searchInput.value.trim() !== q) return;
          renderProducts(products, q);
        });
      }

      if (searchClear) {
        searchClear.addEventListener('click', function () {
          searchInput.value = '';
          updateClearButton();
          clearSearchResults();
          searchInput.focus();
        });
      }

      if (searchInput && searchList && window.Clyro && Clyro.searchProducts) {
        searchInput.addEventListener('input', function () {
          updateClearButton();
          var q = searchInput.value.trim();
          if (timer) clearTimeout(timer);
          if (q.length < 2) {
            clearSearchResults();
            updateClearButton();
            if (q.length > 0) hide(searchIdle);
            return;
          }
          timer = setTimeout(function () { runSearch(q); }, 280);
        });

        searchInput.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            if (!lastProducts.length) return;
            e.preventDefault();
            setActiveIndex(Math.min(activeIndex + 1, lastProducts.length - 1));
          } else if (e.key === 'ArrowUp') {
            if (!lastProducts.length) return;
            e.preventDefault();
            setActiveIndex(Math.max(activeIndex - 1, 0));
          } else if (e.key === 'Enter' && activeIndex >= 0 && lastProducts[activeIndex] && !e.defaultPrevented) {
            var target = lastProducts[activeIndex];
            if (target.url && searchInput.value.trim().length >= 2) {
              e.preventDefault();
              window.location.href = target.url;
            }
          }
        });
      }

      document.addEventListener('keydown', function (e) {
        if (!searchDialog.open) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          closeSearchDialog();
        }
      });
    }

    var group = document.getElementById('shopify-section-group-header-group');
    var sectionWrap = root.closest('.shopify-section');
    if (root.hasAttribute('data-sticky-header')) {
      if (group) group.style.display = 'contents';
      if (sectionWrap) {
        sectionWrap.classList.add('shopify-section-header-sticky');
        sectionWrap.style.position = 'sticky';
        sectionWrap.style.top = '0';
        sectionWrap.style.zIndex = '40';
      }
    } else if (sectionWrap) {
      if (group) group.style.display = '';
      sectionWrap.classList.remove('shopify-section-header-sticky');
      sectionWrap.style.position = '';
      sectionWrap.style.top = '';
      sectionWrap.style.zIndex = '';
    }

    var countBadge = root.querySelector('[data-cart-count]');
    if (countBadge && window.Clyro && Clyro.cart) {
      function updateCount(state) {
        var n = (state && state.item_count) || 0;
        countBadge.textContent = String(n);
        if (n > 0) countBadge.classList.remove('clyro-hidden');
        else countBadge.classList.add('clyro-hidden');
      }
      updateCount(Clyro.cart.state);
      Clyro.on('cart:updated', function (e) { updateCount(e.detail); });
    }
  }

  function boot() {
    document.querySelectorAll('[data-section-type="header"]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
